import { DateTime } from 'luxon';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { DocumentCategory } from '../../../interfaces/core/document-category';
import { DocumentFiles } from '../../../interfaces/core/document-files.entity';
import { DocumentInstitution } from '../../../interfaces/core/document-institution.entity';
import { clientHeaders } from '../../../utils/services';
import { PaginationType } from '../../../constants/types';

export type DownloadDocumentsFilters = {
	id_category?: string;
	id_document?: string;
	id_institution?: string;
}

export type FilterDocumentsType = {
	id_category?: string;
	id_document?: string;
	id_institution?: string;
	uploaded?: boolean;
	no_uploaded?: boolean;
	id_subcategory?: string;
	id_municipality?: string;
	year?: string;
}


@Injectable({
	providedIn: 'root'
})
export class DocumentsService {
	private baseUrl: string = environment.baseUrl;

	constructor(private http: HttpClient) { }

	getDocumentCategories(){
		return this.http.get<DocumentCategory[]>(`${this.baseUrl}/documents/categories`);
	}

	getDocuments(documentParams: PaginationType<FilterDocumentsType>){
		const { filters, ...options } = documentParams;

		if (filters) {
			Object.keys(filters).forEach((key: string) => {
				const filterKey = key as keyof FilterDocumentsType;
				return !filters[filterKey] && delete filters[filterKey];
			});
		}

		// Replace ids
		if (filters?.id_subcategory) {
			filters.id_category = filters.id_subcategory;
			delete filters.id_subcategory;
		}

		if (filters?.no_uploaded) {
			filters.uploaded = false;
		}

		const params = new HttpParams({
			fromObject: JSON.parse(JSON.stringify({
				...options,
				...filters
			}))
		});

		return this.http.get<{count: number, data: DocumentFiles[]}>(`${this.baseUrl}/documents`, {params});
	}

	getDocumentsDownloadXlsx(documentParams: PaginationType<FilterDocumentsType>) {
		const { filters, ...options } = documentParams;
		
		if (filters) {
			Object.keys(filters).forEach((key: string) => {
				const filterKey = key as keyof FilterDocumentsType;
				return !filters[filterKey] && delete filters[filterKey];
			});
		}

		const params = new HttpParams({
			fromObject: JSON.parse(JSON.stringify({
				...options,
				...filters
			}))
		});

		return this.http.get(`${this.baseUrl}/documents/downloadxlsx`, {
			params: params,
			responseType: 'arraybuffer'
		});
	}

	getDocumentsInstitution(institutionId: string, year: string){
		return this.http.get<DocumentInstitution[]>(`${this.baseUrl}/documents/documents-institution/${institutionId}/${year}`);
	}

	uploadDocumentInstitution(
		documentId: string,
		institutionId: string,
		document?: File | null,
		documentInstitutionId?: string | null,
		year?: string | null
	){
		const formData = new FormData();
		formData.append('id_institution', institutionId);
		if (document) {
			formData.append('document', document);
		}
		if (documentInstitutionId) {
			formData.append('id', documentInstitutionId);
		}
		if (year) {
			formData.append('year', year);
		}

		return this.http.post<{
			id: string | null,
			filename: string | null
		}>(`${this.baseUrl}/documents/document-institution/${documentId}`, formData, {
			headers: clientHeaders,
			withCredentials: true
		});
	}

	downloadAll(filters: DownloadDocumentsFilters){
		return this.http.get(`${this.baseUrl}/documents/download`, {
			params: filters,
			responseType: 'arraybuffer'
		}).pipe(
			map((data) => {
				const blob = new Blob([data], { type: 'application/zip' });
				const objectUrl = URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = objectUrl;
				a.download = `documents-${DateTime.now().toISODate()}.zip`;
				a.click();
				URL.revokeObjectURL(objectUrl);
				a.remove();

				return true;
			})
		);
	}

	downloadFile(resourse: string, filename: string): Observable<any>{
		return this.http.get(`${this.baseUrl}/documents/downloadFile/${resourse}/${filename}`, { responseType: 'blob' });
	}

}
